/**
 * Contributor session state.
 *
 * The token pair lives in localStorage; the contributor record is always
 * re-fetched from /auth/me rather than cached, so a stale copy can never show
 * someone the wrong onboarding step or the wrong balance.
 */
import { create } from "zustand";
import { auth as authApi, type SignupPayload } from "../api/endpoints";
import { setUnauthorisedHandler } from "../api/http";
import { clearSession, readSession, writeSession } from "../lib/session";
import type { Contributor } from "../types/api";

interface AuthState {
  contributor: Contributor | null;
  loading: boolean;
  /** True until the first /auth/me settles, so routes don't flash. */
  initialising: boolean;
  error: string | null;
  signup: (payload: SignupPayload) => Promise<Contributor>;
  login: (identifier: string, password: string) => Promise<Contributor>;
  logout: () => Promise<void>;
  refreshContributor: () => Promise<Contributor | null>;
  setContributor: (contributor: Contributor) => void;
  bootstrap: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  contributor: null,
  loading: false,
  initialising: true,
  error: null,

  setContributor: (contributor) => set({ contributor }),

  bootstrap: async () => {
    if (!readSession()) {
      set({ initialising: false });
      return;
    }
    try {
      const contributor = await authApi.me();
      set({ contributor, initialising: false });
    } catch {
      clearSession();
      set({ contributor: null, initialising: false });
    }
  },

  signup: async (payload) => {
    set({ loading: true, error: null });
    try {
      const result = await authApi.signup(payload);
      writeSession(result.tokens);
      set({ contributor: result.contributor, loading: false });
      return result.contributor;
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
      throw err;
    }
  },

  login: async (identifier, password) => {
    set({ loading: true, error: null });
    try {
      const result = await authApi.login(identifier, password);
      writeSession(result.tokens);
      set({ contributor: result.contributor, loading: false });
      return result.contributor;
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
      throw err;
    }
  },

  logout: async () => {
    const session = readSession();
    try {
      // Best effort: revoke server-side, but never leave someone stuck on a
      // screen they asked to leave because the network was down.
      if (session) await authApi.logout(session.refresh_token);
    } catch {
      /* ignore */
    }
    clearSession();
    set({ contributor: null, error: null });
  },

  refreshContributor: async () => {
    if (!readSession()) return null;
    try {
      const contributor = await authApi.me();
      set({ contributor });
      return contributor;
    } catch {
      return get().contributor;
    }
  },
}));

// A refresh token that no longer works drops the session here, so every
// screen reacts the same way instead of each handling 401 itself.
setUnauthorisedHandler(() => {
  useAuth.setState({ contributor: null });
});
